import { useEffect, useState } from "react";
import ReactCountryFlag from "react-country-flag";
import { useTranslation } from "react-i18next";

import type { Commitment, Service, Stat } from "./i18n";

const heroBackground =
  "https://images.unsplash.com/photo-1554284126-aa88f22d8b74?auto=format&fit=crop&w=1920&q=80";
const featureImage =
  "https://images.unsplash.com/photo-1558611848-73f7eb4001a1?auto=format&fit=crop&w=1200&q=80";
const supplementImage =
  "https://images.unsplash.com/photo-1522336572468-97b06e8ef143?auto=format&fit=crop&w=1000&q=80";

const icons: Record<string, string> = {
  "shield-check":
    "M12 21c-6-4-9-7-9-11V5l9-3 9 3v5c0 4-3 7-9 11Zm0 0V9",
  banknote:
    "M3 7h18v10H3V7Zm3 0v10m12-10v10M9 12a3 3 0 1 0 6 0 3 3 0 0 0-6 0Z",
  "globe-2":
    "M12 21a9 9 0 1 1 0-18 9 9 0 0 1 0 18Zm0 0c-2.5 0-4.5-4-4.5-9s2-9 4.5-9m0 18c2.5 0 4.5-4 4.5-9s-2-9-4.5-9m-9 9h18",
  leaf: "M5 21c1-4 2-6 4-8 3-3 7-5 12-6-1 5-3 9-6 12-2 2-4 3-8 4l-2-2Z",
};

function App() {
  const { t, i18n } = useTranslation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const services = t("services.cards", { returnObjects: true }) as Service[];
  const commitments = t("commitments.items", { returnObjects: true }) as Commitment[];
  const chips = t("about.chips", { returnObjects: true }) as string[];
  const stats = t("about.stats", { returnObjects: true }) as Stat[];
  const complianceTicker = t("complianceTicker", { returnObjects: true }) as string[];
  const footerBadges = t("footer.badges", { returnObjects: true }) as string[];

  const toggleLanguage = () => {
    i18n.changeLanguage(i18n.language === "pt" ? "en" : "pt");
  };

  const nextLanguageLabel =
    i18n.language === "pt"
      ? t("translationToggle.english")
      : t("translationToggle.portuguese");

  const navigationItems = [
    { href: "#sobre", label: t("nav.about") },
    { href: "#servicos", label: t("nav.services") },
    { href: "#qualidade", label: t("nav.quality") },
    { href: "#contato", label: t("nav.contact") },
  ];

  useEffect(() => {
    setIsMenuOpen(false);
  }, [i18n.language]);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768) {
        setIsMenuOpen(false);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#FFF9F3] via-[#F4FBFF] to-[#F1FFF6] text-[#101421]">
      <header className="relative overflow-hidden">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `linear-gradient(rgba(14, 24, 40, 0.55), rgba(20, 32, 48, 0.7)), url(${heroBackground})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-12 lg:py-16 flex flex-col gap-10">
          <nav className="flex items-center justify-between text-white">
            <div className="flex items-center gap-3">
              <div className="relative flex h-12 w-12 sm:h-14 sm:w-14 items-center justify-center rounded-2xl bg-white/10 backdrop-blur border border-white/30">
                <svg
                  viewBox="0 0 64 64"
                  className="h-9 w-9 sm:h-10 sm:w-10 text-white"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.2"
                >
                  <circle cx="32" cy="32" r="21" className="opacity-80" />
                  <path d="M20 36c4 6 12 12 12 12s16-12 16-24c0-8-6-12-12-12-4 0-8 2-10 6" className="opacity-80" />
                  <path d="M28 32c0-4 2-8 4-10 2 2 4 6 4 10 0 6-4 10-4 10s-4-4-4-10Z" />
                </svg>
              </div>
              <div>
                <p className="text-base sm:text-lg font-semibold uppercase tracking-[0.4em] text-white">
                  BioVita
                </p>
                <p className="text-[11px] sm:text-xs text-white/70">Global Wellness Trade</p>
              </div>
            </div>

            <div className="flex items-center">
              <button
                type="button"
                onClick={() => setIsMenuOpen((prev) => !prev)}
                className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-white/25 bg-white/10 backdrop-blur text-white transition hover:bg-white/20 sm:hidden"
                aria-label="Abrir menu"
              >
                <svg
                  className="h-6 w-6"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  {isMenuOpen ? (
                    <path d="M18 6 6 18M6 6l12 12" />
                  ) : (
                    <path d="M4 6h16M4 12h16M4 18h16" />
                  )}
                </svg>
              </button>
            </div>
          </nav>

          <div
            className={`sm:hidden ${
              isMenuOpen ? "max-h-96 opacity-100" : "max-h-0 opacity-0"
            } overflow-hidden transition-all duration-300 ease-out`}
          >
            <div className="mt-2 rounded-3xl border border-white/15 bg-white/10 backdrop-blur px-6 py-6 text-xs uppercase tracking-[0.35em] text-white/80 space-y-4">
              {navigationItems.map((item) => (
                <a
                  key={item.href}
                  href={item.href}
                  className="block hover:text-white transition"
                  onClick={() => setIsMenuOpen(false)}
                >
                  {item.label}
                </a>
              ))}
              <button
                type="button"
                onClick={toggleLanguage}
                className="inline-flex items-center gap-2 rounded-full border border-white/30 bg-white/15 px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.3em] text-white/90 transition hover:bg-white/25"
                aria-label={`${t("translationToggle.label")}: ${nextLanguageLabel}`}
              >
                <span className="h-2 w-2 rounded-full bg-[#7EE5D3] animate-pulse" />
                {nextLanguageLabel}
              </button>
            </div>
          </div>

          <div className="hidden md:flex items-center justify-between text-white">
            <div className="flex items-center gap-6 text-xs uppercase tracking-[0.35em] text-white/75">
              {navigationItems.map((item) => (
                <a key={item.href} href={item.href} className="hover:text-white transition">
                  {item.label}
                </a>
              ))}
            </div>
            <button
              type="button"
              onClick={toggleLanguage}
              className="inline-flex items-center gap-2 rounded-full border border-white/30 bg-white/15 px-4 py-2 text-xs font-semibold uppercase tracking-[0.3em] text-white/90 transition hover:bg-white/25"
              aria-label={`${t("translationToggle.label")}: ${nextLanguageLabel}`}
            >
              <span className="h-2 w-2 rounded-full bg-[#7EE5D3] animate-pulse" />
              {nextLanguageLabel}
            </button>
          </div>

          <div className="relative overflow-hidden rounded-3xl border border-white/20 bg-white/10 px-5 sm:px-6 py-8 backdrop-blur-xl">
            <div className="absolute inset-0 opacity-20" style={{ backgroundImage: "radial-gradient(circle at 20% 20%, #7EE5D3, transparent 35%)" }} />
            <div className="absolute inset-0 opacity-20" style={{ backgroundImage: "radial-gradient(circle at 80% 30%, #FF8EC3, transparent 45%)" }} />
            <div className="relative grid gap-10 md:grid-cols-[1.15fr_0.85fr] md:items-center">
              <div className="text-white">
                <span className="inline-flex items-center gap-3 rounded-full bg-white/15 px-3 sm:px-4 py-2 text-[11px] sm:text-sm uppercase tracking-[0.45em] text-white/80">
                  {t("hero.tag")}
                </span>
                <h1 className="mt-5 text-3xl sm:text-5xl lg:text-6xl font-light leading-tight">
                  {t("hero.headline")}
                </h1>
                <p className="mt-5 text-sm sm:text-lg text-white/85 max-w-xl leading-relaxed">
                  {t("hero.subheadline")}
                </p>
                <div className="mt-8 flex flex-wrap items-center gap-4 sm:gap-5">
                  <a
                    href="#sobre"
                    className="inline-flex items-center justify-center rounded-full bg-white px-7 py-3 text-xs font-semibold uppercase tracking-[0.25em] text-[#112039] shadow-lg shadow-[#3ADBCB]/40 transition hover:shadow-xl hover:-translate-y-0.5"
                  >
                    {t("hero.button")}
                  </a>
                  <div className="text-xs sm:text-sm text-white/80 flex items-center gap-3">
                    <div className="h-2 w-2 rounded-full bg-[#FFCE73] animate-[pulse_2.6s_ease-in-out_infinite]" />
                    <span>{t("hero.compliance")}</span>
                  </div>
                </div>
              </div>

              <div className="relative">
                <div className="rounded-[22px] sm:rounded-[28px] border border-white/30 bg-white/15 p-1.5 sm:p-2 backdrop-blur-xl shadow-2xl shadow-[#0F172A]/40">
                  <div className="relative overflow-hidden rounded-[22px]">
                    <img
                      src={featureImage}
                      alt={t("hero.headline")}
                      className="h-[240px] sm:h-[320px] lg:h-[360px] w-full object-cover animate-[float_7s_ease-in-out_infinite]"
                      loading="lazy"
                    />
                    <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-[#0F172A]/80 via-transparent to-transparent px-6 py-6 text-white">
                      <p className="text-xs uppercase tracking-[0.35em] text-white/70">
                        Beauty · Wellness · Performance
                      </p>
                      <p className="mt-2 text-lg font-semibold">
                        Bilateral operations for premium cosmetics and supplements
                      </p>
                    </div>
                  </div>
                </div>
                <div className="absolute -bottom-10 -left-4 sm:-left-10 hidden w-40 sm:w-44 rounded-2xl border border-white/20 bg-white/85 p-4 text-[#101421] shadow-lg md:block animate-[float_6s_ease-in-out_infinite]">
                  <img
                    src={supplementImage}
                    alt="Wellness routine"
                    className="h-20 w-full rounded-xl object-cover"
                    loading="lazy"
                  />
                  <p className="mt-3 text-xs font-semibold uppercase tracking-[0.28em] text-[#57617A]">
                    Wellness lifestyle
                  </p>
                </div>
              </div>
            </div>

            <div className="mt-10 overflow-hidden rounded-full border border-white/25 bg-white/10">
              <div className="marquee flex gap-10 whitespace-nowrap px-10 py-3 text-xs uppercase tracking-[0.4em] text-white/80">
                {complianceTicker.map((item) => (
                  <span key={item}>{item}</span>
                ))}
                {complianceTicker.map((item) => (
                  <span key={`${item}-dup`}>{item}</span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="relative z-10">
        <section
          id="sobre"
          className="max-w-6xl mx-auto px-6 py-20 grid lg:grid-cols-[1.1fr_0.9fr] gap-16 items-center"
        >
          <div className="space-y-6">
            <h2 className="text-3xl sm:text-4xl font-light text-[#132034]">
              {t("about.title")}
            </h2>
            <p className="text-base sm:text-lg text-[#3E4556] leading-relaxed">
              {t("about.paragraphOne")}
            </p>
            <p className="text-base sm:text-lg text-[#3E4556] leading-relaxed">
              {t("about.paragraphTwo")}
            </p>
            <div className="flex flex-wrap gap-3 pt-2">
              {chips.map((chip) => (
                <span
                  key={chip}
                  className="inline-flex items-center gap-2 rounded-full border border-[#D7E6FF] bg-white px-4 py-2 text-xs uppercase tracking-[0.3em] text-[#3C4D6B] shadow-sm"
                >
                  <div className="h-2 w-2 rounded-full bg-[#7EE5D3]" />
                  {chip}
                </span>
              ))}
            </div>
            <div className="grid sm:grid-cols-3 gap-6 pt-6">
              {stats.map((stat) => (
                <div
                  key={stat.label}
                  className="rounded-2xl border border-[#E1E6F6] bg-white/80 p-6 shadow-sm backdrop-blur"
                >
                  <p className="text-3xl font-semibold text-[#132034]">{stat.value}</p>
                  <p className="mt-2 text-sm uppercase tracking-[0.25em] text-[#69738A]">
                    {stat.label}
                  </p>
                </div>
              ))}
            </div>
          </div>

          <div className="relative">
            <div className="absolute -inset-6 rounded-[32px] bg-gradient-to-br from-[#9BE7D7]/40 via-[#FBC2EB]/30 to-[#A6C1EE]/30 blur-3xl" />
            <div className="relative rounded-[32px] border border-[#E3E7F8] bg-white/95 p-10 shadow-xl backdrop-blur">
              <h3 className="text-xl font-semibold text-[#132034]">
                {t("about.missionTitle")}
              </h3>
              <p className="mt-4 text-[#3E4556] leading-relaxed">
                {t("about.missionText")}
              </p>
              <div className="mt-8 grid gap-4 text-sm text-[#3C4D6B]">
                <div className="flex items-center gap-3">
                  <div className="h-8 w-8 rounded-full bg-[#E9F4FF] flex items-center justify-center text-[#4B5F89] font-semibold">
                    BR
                  </div>
                  <span>São Paulo · Rio · Belo Horizonte</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="h-8 w-8 rounded-full bg-[#EFFFF8] flex items-center justify-center text-[#2C725D] font-semibold">
                    USA
                  </div>
                  <span>Orlando · Miami · New York</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section id="servicos" className="bg-white/80 py-20">
          <div className="max-w-6xl mx-auto px-4 sm:px-6">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 sm:gap-10 mb-10 sm:mb-14">
              <div>
                <h2 className="text-3xl sm:text-4xl font-light text-[#132034]">
                  {t("services.introTitle")}
                </h2>
                <p className="mt-4 text-[#3E4556] max-w-2xl leading-relaxed">
                  {t("services.introDescription")}
                </p>
              </div>
              <div className="flex items-center gap-3 rounded-full border border-[#D7E6FF] bg-[#F2F7FF] px-5 py-3 text-sm text-[#415173] shadow-sm">
                <div className="h-10 w-10 rounded-full bg-gradient-to-br from-[#7EE5D3] to-[#5DAAD2] flex items-center justify-center text-white font-semibold">
                  24/7
                </div>
                <span>{t("services.highlight")}</span>
              </div>
            </div>

            <div className="grid gap-6 sm:gap-8 md:grid-cols-2 lg:grid-cols-3">
              {services.map((service) => (
                <article
                  key={service.title}
                  className="group relative overflow-hidden rounded-[26px] border border-[#E4EBFF] bg-gradient-to-br from-white to-[#F7FAFF] p-7 sm:p-8 shadow-[0_30px_60px_-40px_rgba(30,60,114,0.35)] transition hover:-translate-y-1 hover:shadow-[0_35px_80px_-45px_rgba(41,130,164,0.45)]"
                >
                  <div className="absolute -top-24 right-0 h-56 w-56 rounded-full bg-gradient-to-br from-[#FF8EC3]/20 to-[#7EE5D3]/30 blur-3xl" />
                  <div className="relative h-12 w-12 rounded-2xl bg-gradient-to-br from-[#7EE5D3] to-[#49A6F2] text-white flex items-center justify-center text-xl">
                    {service.flag ? (
                      <ReactCountryFlag svg countryCode={service.flag} style={{ width: "1.8rem", height: "1.8rem", borderRadius: "0.75rem", boxShadow: "0 0 10px rgba(17,32,57,0.25)" }} />
                    ) : (
                      <svg
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.4"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="h-6 w-6"
                      >
                        <path d={service.icon} />
                      </svg>
                    )}
                  </div>
                  <h3 className="relative mt-6 text-lg sm:text-xl font-semibold text-[#132034]">
                    {service.title}
                  </h3>
                  <p className="relative mt-3 text-sm sm:text-base text-[#3E4556] leading-relaxed">
                    {service.description}
                  </p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section id="qualidade" className="relative py-20">
          <div className="absolute inset-0 bg-gradient-to-br from-[#101E33] via-[#132034] to-[#0F1A29]" />
          <div className="relative max-w-6xl mx-auto px-6 text-white">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-12">
              <div className="max-w-xl">
                <span className="inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-2 text-xs uppercase tracking-[0.35em] text-white/70">
                  {t("nav.quality")}
                </span>
                <h2 className="mt-6 text-3xl sm:text-4xl font-light leading-tight">
                  {t("commitments.title")}
                </h2>
                <p className="mt-6 text-white/80 leading-relaxed">
                  {t("commitments.description")}
                </p>
              </div>
              <div className="grid sm:grid-cols-2 gap-6 max-w-xl w-full">
                {commitments.map((commitment) => (
                  <article
                    key={commitment.title}
                    className="rounded-[26px] border border-white/10 bg-white/6 p-6 backdrop-blur shadow-[0_25px_60px_-40px_rgba(126,229,211,0.45)]"
                  >
                    <svg
                      className="h-10 w-10 text-[#7EE5D3]"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d={icons[commitment.icon]} />
                    </svg>
                    <h3 className="mt-4 text-lg font-semibold text-white">
                      {commitment.title}
                    </h3>
                    <p className="mt-3 text-sm text-white/75 leading-relaxed">
                      {commitment.description}
                    </p>
                  </article>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section id="contato" className="bg-[#F7FBFF] py-16 sm:py-20">
          <div className="max-w-5xl mx-auto px-4 sm:px-6">
            <div className="rounded-[32px] sm:rounded-[36px] border border-[#DFE8FF] bg-white/95 p-8 sm:p-10 lg:p-12 shadow-[0_30px_60px_-45px_rgba(19,32,52,0.35)] backdrop-blur">
              <div className="grid gap-8 sm:gap-10 lg:grid-cols-[1.15fr_0.85fr]">
                <div className="space-y-5 sm:space-y-6">
                  <h2 className="text-3xl sm:text-4xl font-light text-[#132034]">
                    {t("contact.title")}
                  </h2>
                  <p className="text-sm sm:text-base text-[#3E4556] leading-relaxed">
                    {t("contact.description")}
                  </p>
                </div>
                <div className="space-y-5 sm:space-y-6 text-sm text-[#3E4556]">
                  <div>
                    <p className="uppercase tracking-[0.35em] text-[#7380A1] text-xs">
                      {t("contact.addressLabel")}
                    </p>
                    <p className="mt-2 text-sm sm:text-base lg:text-lg font-medium text-[#132034]">
                      {t("contact.addressValue")}
                    </p>
                  </div>
                  <div>
                    <p className="uppercase tracking-[0.35em] text-[#7380A1] text-xs">
                      {t("contact.emailLabel")}
                    </p>
                    <a
                      href="mailto:contact@biovitacbd.com"
                      className="mt-2 inline-flex items-center gap-2 text-sm sm:text-base lg:text-lg font-medium text-[#132034] hover:text-[#2C725D]"
                    >
                      contact@biovitacbd.com
                    </a>
                  </div>
                  <div className="flex flex-wrap items-center gap-2 sm:gap-3 text-[10px] sm:text-xs uppercase tracking-[0.3em] text-[#7380A1]">
                    <span className="h-2 w-2 rounded-full bg-[#7EE5D3] animate-pulse" />
                    {t("hero.compliance")}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <footer className="bg-[#0C1624] py-10">
        <div className="max-w-6xl mx-auto px-6 flex flex-col gap-6 text-white/80 text-sm sm:flex-row sm:items-center sm:justify-between">
          <p>{t("footer.legal", { year: new Date().getFullYear() })}</p>
          <div className="flex flex-wrap items-center gap-6">
            {footerBadges.map((badge) => (
              <span key={badge} className="uppercase tracking-[0.3em] text-white/60 text-xs">
                {badge}
              </span>
            ))}
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;
